import React, { memo } from "react";

import { formatUnitPrice } from "services/api/pricing/consts";

import { SubscriptionPriceBreakdownProps } from "../../types";

/**
 * Razlaganje jedne vrijednosti po slojevima: cjenik → shema → efektivno.
 *
 * Svaki iznos ovdje dolazi iz resolvera. Komponenta ne zbraja, ne množi i ne zaokružuje —
 * samo ispisuje ono što je backend izračunao.
 */
const SubscriptionPriceBreakdown: React.FC<SubscriptionPriceBreakdownProps> = (props) => {
  const { label, breakdown, unitNote, isLoading } = props;

  if (isLoading) {
    return (
      <div className="border border-slate-200 px-4 py-3">
        <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
          {label}
        </p>
        <div className="h-6 mt-2 w-24 bg-slate-100 animate-pulse" />
      </div>
    );
  }

  if (!breakdown) {
    return (
      <div className="border border-slate-200 px-4 py-3">
        <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
          {label}
        </p>
        <p className="text-sm text-slate-400 mt-2">
          Nije razriješeno — cjenik nema vrijednost za ovaj odabir.
        </p>
      </div>
    );
  }

  const hasSchemeEffect = breakdown.afterScheme !== breakdown.standard;
  const hasOverrideEffect = breakdown.effective !== breakdown.afterScheme;

  return (
    <div className="border border-slate-200 px-4 py-3">
      <p className="text-xs uppercase tracking-wide text-slate-400 font-semibold">
        {label}
      </p>

      <p className="text-2xl font-bold text-slate-900 mt-1">
        {formatUnitPrice.format(breakdown.effective)}
      </p>

      {unitNote && <p className="text-xs text-slate-500">{unitNote}</p>}

      <div className="mt-3 space-y-1 text-xs">
        <div className="flex justify-between text-slate-500">
          <span>Cjenik</span>
          <span>{formatUnitPrice.format(breakdown.standard)}</span>
        </div>

        {hasSchemeEffect && (
          <div className="flex justify-between text-slate-500">
            <span>Nakon grupe</span>
            <span>{formatUnitPrice.format(breakdown.afterScheme)}</span>
          </div>
        )}

        {hasOverrideEffect && (
          <div className="flex justify-between text-slate-500">
            <span>Nakon posebnih uvjeta</span>
            <span>{formatUnitPrice.format(breakdown.effective)}</span>
          </div>
        )}

        {breakdown.discountPercent !== 0 && (
          <div className="flex justify-between text-slate-700 font-medium pt-1 border-t border-slate-100">
            <span>Ukupan popust</span>
            <span>
              {breakdown.discountPercent.toFixed(1).replace(".", ",")} %
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default memo(SubscriptionPriceBreakdown);
